import { ContainerComponent, Loader } from 'component'
import { toPng } from 'html-to-image'
import PptxGenJS from 'pptxgenjs'

const SLIDE_WIDTH = 13.33
const SLIDE_HEIGHT = 7.5

const HEADER_HEIGHT = 0.6
const FOOTER_HEIGHT = 0.4
const CONTENT_MARGIN = 0.3
const CONTENT_TOP = HEADER_HEIGHT + CONTENT_MARGIN
const CONTENT_HEIGHT = SLIDE_HEIGHT - CONTENT_TOP - FOOTER_HEIGHT - CONTENT_MARGIN

const NATIVE_CHART_TYPES: Record<string, string> = {
	column: 'bar',
	bar: 'bar',
	line: 'line',
	area: 'area',
	pie: 'pie',
	doughnut: 'doughnut',
	spline: 'line',
	areaspline: 'area',
	scatter: 'scatter',
	bubble: 'bubble',
}

const getFontFamily = (): string => {
	const customFont = getComputedStyle(document.documentElement).getPropertyValue('--custom-font').trim()
	return customFont || 'Roboto'
}

const getPrimaryColor = (): string => {
	const color = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim()
	return color.replace('#', '') || 'f50537'
}

const getLogoBase64 = (): string | null => {
	const logoImg = document.querySelector<HTMLImageElement>('.js-header-logo img')
	if (!logoImg) {
		return null
	}

	try {
		const canvas = document.createElement('canvas')
		canvas.width = logoImg.naturalWidth || logoImg.width
		canvas.height = logoImg.naturalHeight || logoImg.height
		const ctx = canvas.getContext('2d')
		if (!ctx) {
			return null
		}
		ctx.drawImage(logoImg, 0, 0)
		return canvas.toDataURL('image/png')
	} catch {
		const src = logoImg.src
		if (src.startsWith('data:')) {
			return src
		}
		return null
	}
}

const rgbToHex = (rgb: string): string => {
	if (rgb.startsWith('#')) {
		return rgb.replace('#', '')
	}
	const match = rgb.match(/\d+/g)
	if (!match || match.length < 3) {
		return 'FFFFFF'
	}
	return match
		.slice(0, 3)
		.map((n) => parseInt(n, 10).toString(16).padStart(2, '0'))
		.join('')
}

interface HeaderStyle {
	text: string
	fontSize: number
	fontWeight: string
	color: string
	textAlign: string
	fontFamily: string
}

interface BlockHeaderInfo {
	title: HeaderStyle | null
	subtitle: HeaderStyle | null
}

const extractElementStyle = (el: HTMLElement): HeaderStyle => {
	const computed = getComputedStyle(el)
	return {
		text: el.innerText?.trim() || '',
		fontSize: parseFloat(computed.fontSize) || 14,
		fontWeight: computed.fontWeight || '400',
		color: rgbToHex(computed.color || 'rgb(0,0,0)'),
		textAlign: computed.textAlign || 'left',
		fontFamily: computed.fontFamily?.split(',')[0]?.replace(/['"]/g, '').trim() || getFontFamily(),
	}
}

const getBlockHeaderInfo = (blockEl: HTMLElement): BlockHeaderInfo => {
	const head = blockEl.querySelector<HTMLElement>('.js-resize-item-head')
	if (!head) {
		return { title: null, subtitle: null }
	}

	const children = Array.from(
		head.querySelectorAll<HTMLElement>(':scope > div > div, :scope > div > span, :scope > div > p'),
	)

	if (children.length >= 2) {
		const titleEl = children[0]
		const subtitleEl = children[1]
		const titleStyle = extractElementStyle(titleEl)
		const subtitleStyle = extractElementStyle(subtitleEl)

		return {
			title: titleStyle.text ? titleStyle : null,
			subtitle: subtitleStyle.text ? subtitleStyle : null,
		}
	}

	if (children.length === 1) {
		const titleStyle = extractElementStyle(children[0])
		return {
			title: titleStyle.text ? titleStyle : null,
			subtitle: null,
		}
	}

	const innerText = head.innerText?.trim() || ''
	if (innerText) {
		return {
			title: {
				text: innerText,
				fontSize: 14,
				fontWeight: '700',
				color: '393939',
				textAlign: 'left',
				fontFamily: getFontFamily(),
			},
			subtitle: null,
		}
	}

	return { title: null, subtitle: null }
}

const getBlockFooterText = (blockEl: HTMLElement): string => {
	const footer = blockEl.querySelector<HTMLElement>('.js-resize-item-footer')
	return footer?.innerText?.trim() || ''
}

const getHighchartInstance = (blockEl: HTMLElement): Highcharts.Chart | null => {
	const container = blockEl.querySelector<HTMLElement>('.graphics-container')
	if (!container || !window.Highcharts?.charts) {
		return null
	}

	for (const chart of window.Highcharts.charts) {
		if (chart && chart.container && container.contains(chart.container)) {
			return chart
		}
	}
	return null
}

const getChartImageBase64 = async (chart: Highcharts.Chart): Promise<string | null> => {
	return new Promise((resolve) => {
		try {
			const svg = (chart as any).getSVG({
				chart: {
					width: 900,
					height: 500,
				},
			})

			const canvas = document.createElement('canvas')
			canvas.width = 1800
			canvas.height = 1000
			const ctx = canvas.getContext('2d')
			if (!ctx) {
				resolve(null)
				return
			}

			const img = new Image()
			img.onload = () => {
				ctx.fillStyle = '#ffffff'
				ctx.fillRect(0, 0, canvas.width, canvas.height)
				ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
				resolve(canvas.toDataURL('image/png'))
			}
			img.onerror = () => resolve(null)
			img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)))
		} catch {
			resolve(null)
		}
	})
}

const pxToPoints = (px: number): number => Math.round(px * 0.75 * 10) / 10

const mapTextAlign = (align: string): 'left' | 'center' | 'right' => {
	if (align === 'center' || align === 'start') {
		return 'center'
	}
	if (align === 'right' || align === 'end') {
		return 'right'
	}
	return 'left'
}

const addSlideHeader = (
	slide: PptxGenJS.Slide,
	headerInfo: BlockHeaderInfo,
	fontFamily: string,
	primaryColor: string,
	logoBase64: string | null,
): void => {
	let textX = 0.3

	if (logoBase64) {
		slide.addImage({
			data: logoBase64,
			x: 0.3,
			y: 0.1,
			w: 0.4,
			h: 0.4,
			sizing: { type: 'contain', w: 0.4, h: 0.4 },
		})
		textX = 1.2
	}

	const hasSubtitle = headerInfo.subtitle && headerInfo.subtitle.text
	const textW = SLIDE_WIDTH - textX - 0.3

	if (headerInfo.title && headerInfo.title.text) {
		const titleFontSize = pxToPoints(headerInfo.title.fontSize)
		const isBold =
			headerInfo.title.fontWeight === '700' ||
			headerInfo.title.fontWeight === 'bold' ||
			parseInt(headerInfo.title.fontWeight, 10) >= 600

		slide.addText(headerInfo.title.text, {
			x: textX,
			y: 0.05,
			w: textW,
			h: hasSubtitle ? 0.25 : 0.4,
			fontSize: Math.min(titleFontSize, 16),
			fontFace: headerInfo.title.fontFamily || fontFamily,
			bold: isBold,
			color: headerInfo.title.color || '393939',
			align: mapTextAlign(headerInfo.title.textAlign),
			valign: hasSubtitle ? 'bottom' : 'middle',
		})
	}

	if (hasSubtitle) {
		const subtitleFontSize = pxToPoints(headerInfo.subtitle!.fontSize)
		const isSubBold =
			headerInfo.subtitle!.fontWeight === '700' ||
			headerInfo.subtitle!.fontWeight === 'bold' ||
			parseInt(headerInfo.subtitle!.fontWeight, 10) >= 600

		slide.addText(headerInfo.subtitle!.text, {
			x: textX,
			y: 0.3,
			w: textW,
			h: 0.22,
			fontSize: Math.min(subtitleFontSize, 11),
			fontFace: headerInfo.subtitle!.fontFamily || fontFamily,
			bold: isSubBold,
			color: headerInfo.subtitle!.color || '666666',
			align: mapTextAlign(headerInfo.subtitle!.textAlign),
			valign: 'top',
		})
	}

	slide.addShape('line' as any, {
		x: 0.3,
		y: HEADER_HEIGHT,
		w: SLIDE_WIDTH - 0.6,
		h: 0,
		line: { color: primaryColor, width: 1.5 },
	})
}

const getBlockFooterLineCount = (footerText: string): number => {
	if (!footerText) {
		return 0
	}
	return footerText.split('\n').filter((line) => line.trim() !== '').length
}

const getBlockFooterHeight = (footerText: string): number => {
	const lineCount = getBlockFooterLineCount(footerText)
	if (lineCount === 0) {
		return 0
	}
	return lineCount * 0.15 + 0.05
}

const addSlideFooter = (
	slide: PptxGenJS.Slide,
	footerText: string,
	slideIndex: number,
	totalSlides: number,
	fontFamily: string,
	primaryColor: string,
): void => {
	const footerY = SLIDE_HEIGHT - FOOTER_HEIGHT
	const blockFooterHeight = getBlockFooterHeight(footerText)

	if (footerText) {
		slide.addText(footerText, {
			x: 0.3,
			y: footerY - blockFooterHeight,
			w: SLIDE_WIDTH - 0.6,
			h: blockFooterHeight,
			fontSize: 8,
			fontFace: fontFamily,
			color: '666666',
			valign: 'bottom',
		})
	}

	slide.addShape('line' as any, {
		x: 0.3,
		y: footerY,
		w: SLIDE_WIDTH - 0.6,
		h: 0,
		line: { color: primaryColor, width: 1 },
	})

	slide.addText(`${slideIndex} / ${totalSlides}`, {
		x: SLIDE_WIDTH / 2 - 1,
		y: footerY + 0.05,
		w: 2,
		h: 0.3,
		fontSize: 9,
		fontFace: fontFamily,
		color: '999999',
		align: 'center',
		valign: 'middle',
	})

	const dateStr = new Date().toLocaleDateString('en-GB', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	})
	slide.addText(dateStr, {
		x: SLIDE_WIDTH - 2.3,
		y: footerY + 0.05,
		w: 2,
		h: 0.3,
		fontSize: 9,
		fontFace: fontFamily,
		color: '999999',
		align: 'right',
		valign: 'middle',
	})
}

const highchartsToNativeChart = (
	chart: Highcharts.Chart,
): { chartType: string; data: PptxGenJS.OptsChartData[]; opts: PptxGenJS.IChartOpts } | null => {
	const options = chart.options
	const chartType = (options.chart?.type || 'line') as string

	const pptxChartType = NATIVE_CHART_TYPES[chartType]
	if (!pptxChartType) {
		return null
	}

	const series = chart.series
	if (!series?.length) {
		return null
	}

	const data: PptxGenJS.OptsChartData[] = series
		.filter((s) => s.visible !== false && s.name !== 'Navigator')
		.map((s) => {
			const points = s.points || []
			const labels: string[] = []
			const values: number[] = []

			for (const point of points) {
				labels.push(point.name || point.category?.toString() || '')
				values.push(typeof point.y === 'number' ? point.y : 0)
			}

			return {
				name: s.name || '',
				labels,
				values,
			}
		})

	const firstData = data[0]
	if (!data.length || !firstData || !firstData.values?.length) {
		return null
	}

	const isHorizontalBar = chartType === 'bar'

	const stacking =
		(options.plotOptions as any)?.[chartType]?.stacking || (options.plotOptions as any)?.series?.stacking || null

	let barGrouping: 'clustered' | 'stacked' | 'percentStacked' | undefined
	if (stacking === 'percent') {
		barGrouping = 'percentStacked'
	} else if (stacking) {
		barGrouping = 'stacked'
	}

	const yAxis = Array.isArray(chart.yAxis) ? chart.yAxis[0] : null
	const xAxis = Array.isArray(chart.xAxis) ? chart.xAxis[0] : null
	const yAxisOptions = (Array.isArray(options.yAxis) ? options.yAxis[0] : options.yAxis) as any
	const xAxisOptions = (Array.isArray(options.xAxis) ? options.xAxis[0] : options.xAxis) as any
	const valAxisTitle = yAxisOptions?.title?.text || ''
	const catAxisTitle = xAxisOptions?.title?.text || ''
	const valAxisMax =
		typeof yAxisOptions?.max === 'number' ? yAxisOptions.max : typeof yAxis?.max === 'number' ? yAxis.max : undefined
	const valAxisMin =
		typeof yAxisOptions?.min === 'number' ? yAxisOptions.min : typeof yAxis?.min === 'number' ? yAxis.min : undefined

	const opts: PptxGenJS.IChartOpts = {
		x: 0.5,
		y: CONTENT_TOP,
		w: SLIDE_WIDTH - 1,
		h: CONTENT_HEIGHT,
		showLegend: series.length > 1,
		legendPos: 'b',
		legendFontSize: 8,
		showTitle: false,
		barDir: isHorizontalBar ? 'bar' : 'col',
		barGrouping: barGrouping,
		catAxisLabelFontSize: 9,
		valAxisLabelFontSize: 9,
		dataLabelFontSize: 8,
		chartColors: extractChartColors(chart),
	}

	if (barGrouping !== 'percentStacked') {
		if (typeof valAxisMax === 'number') {
			opts.valAxisMaxVal = valAxisMax
		}
		if (typeof valAxisMin === 'number') {
			opts.valAxisMinVal = valAxisMin
		}
	}

	if (valAxisTitle) {
		opts.showValAxisTitle = true
		opts.valAxisTitle = valAxisTitle
		opts.valAxisTitleFontSize = 9
	}
	if (catAxisTitle) {
		opts.showCatAxisTitle = true
		opts.catAxisTitle = catAxisTitle
		opts.catAxisTitleFontSize = 9
	}

	if (chartType === 'pie' || chartType === 'doughnut') {
		opts.showPercent = true
		opts.showLegend = true
	}

	return { chartType: pptxChartType, data, opts }
}

const colorToHex = (color: any): string => {
	if (!color) {
		return ''
	}
	// Handle Highcharts gradient objects like { linearGradient: ..., stops: [...] }
	if (typeof color === 'object') {
		if (color.stops && color.stops.length) {
			return colorToHex(color.stops[0][1])
		}
		if (color.color) {
			return colorToHex(color.color)
		}
		return ''
	}
	if (typeof color !== 'string') {
		return ''
	}
	if (color.startsWith('#')) {
		return color.replace('#', '')
	}
	const match = color.match(/\d+/g)
	if (match && match.length >= 3) {
		return match
			.slice(0, 3)
			.map((n: string) => parseInt(n, 10).toString(16).padStart(2, '0'))
			.join('')
	}
	return ''
}

const extractChartColors = (chart: Highcharts.Chart): string[] => {
	const chartType = (chart.options.chart?.type || 'line') as string
	const isPieType = chartType === 'pie' || chartType === 'doughnut'

	if (isPieType && chart.series?.[0]?.points?.length) {
		const pointColors = chart.series[0].points.map((p: any) => colorToHex(p.color)).filter((c: string) => c)
		if (pointColors.length) {
			return pointColors
		}
	}

	const visibleSeries = chart.series.filter((s) => s.visible !== false && s.name !== 'Navigator')

	const seriesColors = visibleSeries.map((s: any) => colorToHex(s.color)).filter((c: string) => c)
	if (seriesColors.length) {
		return seriesColors
	}

	// Try colors from series options
	const optionColors = visibleSeries.map((s: any) => colorToHex(s.options?.color)).filter((c: string) => c)
	if (optionColors.length) {
		return optionColors
	}

	// Try chart-level colors option
	if (chart.options.colors?.length) {
		const chartColors = chart.options.colors.map((c: any) => colorToHex(c)).filter((c: string) => c)
		if (chartColors.length) {
			return chartColors
		}
	}

	const globalColors = window.Highcharts?.getOptions()?.colors || []
	return globalColors.map((c: any) => colorToHex(c) || 'f50537')
}

const addChartToSlide = async (
	slide: PptxGenJS.Slide,
	blockEl: HTMLElement,
	fontFamily: string,
	effectiveContentHeight: number,
): Promise<void> => {
	const chart = getHighchartInstance(blockEl)
	if (!chart) {
		return
	}

	const nativeChart = highchartsToNativeChart(chart)
	if (nativeChart) {
		nativeChart.opts.h = effectiveContentHeight
		nativeChart.opts.catAxisLabelFontFace = fontFamily
		nativeChart.opts.valAxisLabelFontFace = fontFamily
		slide.addChart(nativeChart.chartType as any, nativeChart.data, nativeChart.opts)
		return
	}

	const imageBase64 = await getChartImageBase64(chart)
	if (imageBase64) {
		// Chart SVG is rendered at 1800x1000 — preserve 1.8:1 aspect ratio
		const chartAspect = 1800 / 1000
		const maxW = SLIDE_WIDTH - 1
		const maxH = effectiveContentHeight
		let imgW: number
		let imgH: number

		if (chartAspect > maxW / maxH) {
			imgW = maxW
			imgH = maxW / chartAspect
		} else {
			imgH = maxH
			imgW = maxH * chartAspect
		}

		const imgX = 0.5 + (maxW - imgW) / 2
		const imgY = CONTENT_TOP + (maxH - imgH) / 2
		slide.addImage({ data: imageBase64, x: imgX, y: imgY, w: imgW, h: imgH })
	}
}

const addNumberToSlide = (
	slide: PptxGenJS.Slide,
	blockEl: HTMLElement,
	fontFamily: string,
	effectiveContentHeight: number,
): void => {
	const resultEl = blockEl.querySelector<HTMLElement>('.result')
	const titleEl = blockEl.querySelector<HTMLElement>('.title')

	const resultText = resultEl?.textContent?.trim() || ''
	const titleText = titleEl?.textContent?.trim() || ''

	const primaryColor = getPrimaryColor()

	if (resultText) {
		slide.addText(resultText, {
			x: 0.5,
			y: CONTENT_TOP + effectiveContentHeight * 0.15,
			w: SLIDE_WIDTH - 1,
			h: effectiveContentHeight * 0.45,
			fontSize: 48,
			fontFace: fontFamily,
			bold: true,
			color: primaryColor,
			align: 'center',
			valign: 'middle',
		})
	}

	if (titleText) {
		slide.addText(titleText, {
			x: 0.5,
			y: CONTENT_TOP + effectiveContentHeight * 0.6,
			w: SLIDE_WIDTH - 1,
			h: effectiveContentHeight * 0.25,
			fontSize: 18,
			fontFace: fontFamily,
			color: '666666',
			align: 'center',
			valign: 'top',
		})
	}
}

const addTableToSlide = (
	slide: PptxGenJS.Slide,
	blockEl: HTMLElement,
	fontFamily: string,
	effectiveContentHeight: number,
): void => {
	const table = blockEl.querySelector<HTMLTableElement>('table')
	if (!table) {
		const textContent = blockEl.querySelector<HTMLElement>('.js-resize-item-block-content')?.innerText?.trim()
		if (textContent) {
			addTextContent(slide, textContent, fontFamily, effectiveContentHeight)
		}
		return
	}

	const rows: PptxGenJS.TableRow[] = []
	const primaryColor = getPrimaryColor()

	const headerRows = table.querySelectorAll<HTMLTableRowElement>('thead tr')
	for (const tr of Array.from(headerRows)) {
		const cells: PptxGenJS.TableCell[] = []
		const ths = tr.querySelectorAll<HTMLTableCellElement>('th')
		for (const th of Array.from(ths)) {
			cells.push({
				text: th.textContent?.trim() || '',
				options: {
					bold: true,
					fontSize: 9,
					fontFace: fontFamily,
					fill: { color: primaryColor },
					color: 'FFFFFF',
					border: [
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
					],
					valign: 'middle',
					colspan: th.colSpan > 1 ? th.colSpan : undefined,
					rowspan: th.rowSpan > 1 ? th.rowSpan : undefined,
				},
			})
		}
		if (cells.length) {
			rows.push(cells)
		}
	}

	const bodyRows = table.querySelectorAll<HTMLTableRowElement>('tbody tr')
	for (const [idx, tr] of Array.from(bodyRows).entries()) {
		const cells: PptxGenJS.TableCell[] = []
		const tds = tr.querySelectorAll<HTMLTableCellElement>('th, td')
		for (const td of Array.from(tds)) {
			cells.push({
				text: td.textContent?.trim() || '',
				options: {
					fontSize: 8,
					fontFace: fontFamily,
					color: '393939',
					fill: { color: idx % 2 === 0 ? 'FFFFFF' : 'F5F5F5' },
					border: [
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
						{ type: 'solid', pt: 0.5, color: 'CCCCCC' },
					],
					valign: 'middle',
					colspan: td.colSpan > 1 ? td.colSpan : undefined,
					rowspan: td.rowSpan > 1 ? td.rowSpan : undefined,
				},
			})
		}
		if (cells.length) {
			rows.push(cells)
		}
	}

	if (rows.length) {
		slide.addTable(rows, {
			x: 0.3,
			y: CONTENT_TOP,
			w: SLIDE_WIDTH - 0.6,
			colW: Array(rows[0].length).fill((SLIDE_WIDTH - 0.6) / rows[0].length),
			autoPage: true,
			autoPageRepeatHeader: true,
			fontSize: 8,
			fontFace: fontFamily,
		})
	}
}

const addTextContent = (
	slide: PptxGenJS.Slide,
	text: string,
	fontFamily: string,
	effectiveContentHeight: number,
): void => {
	slide.addText(text, {
		x: 0.5,
		y: CONTENT_TOP,
		w: SLIDE_WIDTH - 1,
		h: effectiveContentHeight,
		fontSize: 14,
		fontFace: fontFamily,
		color: '393939',
		valign: 'top',
		wrap: true,
	})
}

const addTextBlockToSlide = (
	slide: PptxGenJS.Slide,
	blockEl: HTMLElement,
	fontFamily: string,
	effectiveContentHeight: number,
): void => {
	const content = blockEl.querySelector<HTMLElement>('.js-resize-item-block-content')
	const text = content?.innerText?.trim() || ''
	if (text) {
		addTextContent(slide, text, fontFamily, effectiveContentHeight)
	}
}

const captureBlockAsImage = async (
	el: HTMLElement,
): Promise<{ data: string; srcWidth: number; srcHeight: number } | null> => {
	try {
		const rect = el.getBoundingClientRect()
		const pixelRatio = 2
		const dataUrl = await toPng(el, {
			width: rect.width,
			height: rect.height,
			pixelRatio: pixelRatio,
			cacheBust: true,
			backgroundColor: '#ffffff',
		})
		return {
			data: dataUrl,
			srcWidth: rect.width * pixelRatio,
			srcHeight: rect.height * pixelRatio,
		}
	} catch {
		return null
	}
}

const addBlockImageToSlide = async (
	slide: PptxGenJS.Slide,
	blockEl: HTMLElement,
	effectiveContentHeight: number,
): Promise<void> => {
	const contentEl = blockEl.querySelector<HTMLElement>('.js-resize-item-block-content') || blockEl
	const head = contentEl.querySelector<HTMLElement>('.js-resize-item-head')
	const headParent = head?.parentElement || null
	const headNextSibling = head?.nextSibling || null
	if (head && headParent) {
		head.remove()
	}
	let result: { data: string; srcWidth: number; srcHeight: number } | null = null
	try {
		result = await captureBlockAsImage(contentEl)
	} finally {
		if (head && headParent) {
			headParent.insertBefore(head, headNextSibling)
		}
	}
	if (!result) {
		return
	}

	const maxW = SLIDE_WIDTH - 1
	const maxH = effectiveContentHeight
	const aspectRatio = result.srcWidth / result.srcHeight
	let imgW: number
	let imgH: number

	if (aspectRatio > maxW / maxH) {
		imgW = maxW
		imgH = maxW / aspectRatio
	} else {
		imgH = maxH
		imgW = maxH * aspectRatio
	}

	const imgX = 0.5 + (maxW - imgW) / 2
	const imgY = CONTENT_TOP + (maxH - imgH) / 2

	slide.addImage({ data: result.data, x: imgX, y: imgY, w: imgW, h: imgH })
}

const detectBlockType = (blockEl: HTMLElement): 'chart' | 'number' | 'table' | 'image' | 'text' => {
	if (blockEl.querySelector('.graphics-container')) {
		return 'chart'
	}
	if (blockEl.querySelector('.numbers .result')) {
		return 'number'
	}
	if (blockEl.querySelector('table') || blockEl.querySelector('.datatable')) {
		return 'table'
	}
	// Check if block has meaningful plain text (using innerText to skip style tags)
	const content = blockEl.querySelector<HTMLElement>('.js-resize-item-block-content')
	const plainText = content?.innerText?.trim() || ''
	// If block has visual content (imgs, SVGs, canvas) or no plain text — capture as image
	const hasVisualContent = blockEl.querySelector('img, svg, canvas, .chart, .graphic, .emoji, .icon')
	if (hasVisualContent || !plainText) {
		return 'image'
	}
	return 'text'
}

const isBlockVisible = (item: HTMLElement): boolean => {
	const rect = item.getBoundingClientRect()
	if (rect.width === 0 && rect.height === 0) {
		return false
	}
	const style = getComputedStyle(item)
	if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
		return false
	}
	return true
}

const shouldSkipBlock = (item: HTMLElement): boolean => {
	if (item.classList.contains('js-grid-stack-item-header')) {
		return true
	}
	if (item.classList.contains('js-grid-stack-item-footer')) {
		return true
	}
	if (item.classList.contains('hide-in-pdf')) {
		return true
	}
	if (item.classList.contains('js-grid-stack-new-block')) {
		return true
	}
	if (!isBlockVisible(item)) {
		return true
	}
	return false
}

const getBlockElements = (): HTMLElement[] => {
	const gridStack = document.querySelector<HTMLElement>('.js-grid-stack')
	if (!gridStack) {
		return []
	}

	const items = gridStack.querySelectorAll<HTMLElement>(':scope > .grid-stack-item')
	const blocks: HTMLElement[] = []

	for (const item of Array.from(items)) {
		if (shouldSkipBlock(item)) {
			continue
		}

		const isWrapper = item.querySelector<HTMLElement>('.grid-stack-nested, .grid-stack-sub-grid')
		if (isWrapper) {
			const nestedItems = item.querySelectorAll<HTMLElement>('.grid-stack-item')
			for (const nested of Array.from(nestedItems)) {
				if (!shouldSkipBlock(nested)) {
					blocks.push(nested)
				}
			}
		} else {
			blocks.push(item)
		}
	}

	return blocks
}

const createTitleSlide = (
	pptx: PptxGenJS,
	fontFamily: string,
	primaryColor: string,
	logoBase64: string | null,
): void => {
	const slide = pptx.addSlide()

	if (logoBase64) {
		slide.addImage({
			data: logoBase64,
			x: SLIDE_WIDTH / 2 - 0.5,
			y: 1.5,
			w: 1.5,
			h: 1.5,
			sizing: { type: 'contain', w: 1.5, h: 1.5 },
		})
	}

	const pageTitle = document.title || 'Export'
	slide.addText(pageTitle, {
		x: 1,
		y: logoBase64 ? 3.3 : 2.5,
		w: SLIDE_WIDTH - 2,
		h: 1,
		fontSize: 28,
		fontFace: fontFamily,
		bold: true,
		color: primaryColor,
		align: 'center',
		valign: 'middle',
	})

	const dateStr = new Date().toLocaleDateString('en-GB', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	})
	slide.addText(dateStr, {
		x: 1,
		y: logoBase64 ? 4.3 : 3.5,
		w: SLIDE_WIDTH - 2,
		h: 0.5,
		fontSize: 14,
		fontFace: fontFamily,
		color: '999999',
		align: 'center',
		valign: 'top',
	})
}

export const exportPptx = async (): Promise<void> => {
	Loader.create(ContainerComponent.getContainer('loader'), { absolute: true })

	try {
		const pptx = new PptxGenJS()
		pptx.layout = 'LAYOUT_WIDE'

		const fontFamily = getFontFamily()
		const primaryColor = getPrimaryColor()
		const logoBase64 = getLogoBase64()

		createTitleSlide(pptx, fontFamily, primaryColor, logoBase64)

		const blocks = getBlockElements()
		const totalSlides = blocks.length

		for (let i = 0; i < blocks.length; i++) {
			const blockEl = blocks[i]
			const slide = pptx.addSlide()

			const blockContent = blockEl.querySelector<HTMLElement>('.js-resize-item-block')
			const bg = blockContent?.style.backgroundColor
			if (bg && bg !== 'transparent') {
				slide.background = { fill: rgbToHex(bg) }
			}

			const headerInfo = getBlockHeaderInfo(blockEl)
			const footerText = getBlockFooterText(blockEl)

			addSlideHeader(slide, headerInfo, fontFamily, primaryColor, logoBase64)
			addSlideFooter(slide, footerText, i + 1, totalSlides, fontFamily, primaryColor)

			const blockFooterHeight = getBlockFooterHeight(footerText)
			const effectiveContentHeight = CONTENT_HEIGHT - blockFooterHeight

			const blockType = detectBlockType(blockEl)

			switch (blockType) {
				case 'chart':
					await addChartToSlide(slide, blockEl, fontFamily, effectiveContentHeight)
					break
				case 'number':
					addNumberToSlide(slide, blockEl, fontFamily, effectiveContentHeight)
					break
				case 'table':
					addTableToSlide(slide, blockEl, fontFamily, effectiveContentHeight)
					break
				case 'image':
					await addBlockImageToSlide(slide, blockEl, effectiveContentHeight)
					break
				case 'text':
					addTextBlockToSlide(slide, blockEl, fontFamily, effectiveContentHeight)
					break
			}
		}

		const fileName = document.title ? `${document.title.replace(/[^a-zA-Z0-9-_ ]/g, '')}.pptx` : 'export.pptx'
		await pptx.writeFile({ fileName })
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('PPTX export failed:', error)
	} finally {
		Loader.destroy(ContainerComponent.getContainer('loader'))
	}
}
